import {
    Directive,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    RendererFactory2,
    SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SvgSpriteService } from 'Angular/services/svg-sprite.service';

/**
 *
 * Icon Directive
 *
 * Displays an icon svg using an inlined sprite for better performance
 *
 * Input Properties:
 *
 *  name: The name of the icon (e.g. "icon-save")
 *
 */

@Directive({
    selector: 'svg[ycnIcon]',
    standalone: false,
})
export class IconDirective implements OnInit, OnChanges, OnDestroy {
    @Input() name!: string;

    private iconSVG: SVGGElement | undefined = undefined;

    private renderer: Renderer2;

    private XML_NAMESPACE: string = 'http://www.w3.org/2000/svg';

    private destroy$ = new Subject<void>();

    private spriteReady = false;

    constructor(
        private el: ElementRef,
        private rendererFactory: RendererFactory2,
        private svgSpriteService: SvgSpriteService
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    ngOnInit(): void {
        // Initialize the sprite service - the service handles idempotency internally
        this.svgSpriteService.inlineSprite();

        // Subscribe to sprite load status
        this.svgSpriteService
            .isSpriteLoaded()
            .pipe(takeUntil(this.destroy$))
            .subscribe((loaded: boolean) => {
                if (loaded && !this.spriteReady) {
                    this.spriteReady = true;
                    // Create the icon once sprite is ready
                    this.createIcon();
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.name?.currentValue !== changes?.name?.previousValue) {
            if (this.iconSVG) this.destroyIcon();
            this.createIcon();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    createIcon(): void {
        // Don't create icon if sprite isn't ready yet
        if (!this.spriteReady) {
            return;
        }

        this.renderer.setAttribute(this.el.nativeElement, 'xmlns', this.XML_NAMESPACE);
        this.renderer.addClass(this.el.nativeElement, this.name);

        // Always use inline mode - create the reference to the inlined sprite
        const inlineUrl = this.svgSpriteService.getInlineReference(this.name);
        this.createSVGElement(inlineUrl);
    }

    /**
     * Create icon synchronously without waiting for sprite to load.
     * Use this when manually instantiating the directive and you know the sprite is already loaded.
     */
    createIconSync(): void {
        this.renderer.setAttribute(this.el.nativeElement, 'xmlns', this.XML_NAMESPACE);
        this.renderer.addClass(this.el.nativeElement, this.name);

        // Always use inline mode - create the reference to the inlined sprite
        const inlineUrl = this.svgSpriteService.getInlineReference(this.name);
        this.createSVGElement(inlineUrl);
    }

    createSVGElement(url: string): void {
        /**
         * 1. Sets the <svg> element class and href attributes.
         * 2. Creates the <use> element as a child of the <svg> element.
         */
        this.iconSVG = this.renderer.createElement('use', this.XML_NAMESPACE);
        this.renderer.setAttribute(this.iconSVG, 'href', url);
        this.renderer.appendChild(this.el.nativeElement, this.iconSVG);
    }

    destroyIcon(): void {
        this.renderer.removeChild(this.el.nativeElement, this.iconSVG);
        this.iconSVG = undefined;
    }
}
